import Header from "../../structures/header/index";
import Footer from "../../structures/footer/index";
import { InfiniteSlider } from "../../InfiniteSlider";
import { CircularGallery } from "../../CircularGallery";
import { ProjectShowcase } from "../../ProjectShowcase";
import { ScrollMorphHero } from "../../ScrollMorphHero";
import { LuneScrollFX } from "../../LuneScrollFX";
import { WorldMap } from "../../WorldMap";
import { useEffect } from "react";
import "../../../assets/css/landing.css";

const SERVICES = [
  {
    common: "Sistem UMKM",
    binomial: "KASIR · INVENTORI · KEUANGAN",
    photo: {
      url: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&q=80",
      text: "Point of sale system for UMKM",
      pos: "center",
      by: "Kasir digital & manajemen stok real-time",
    },
  },
  {
    common: "Web Development",
    binomial: "LANDING PAGE · COMPANY PROFILE",
    photo: {
      url: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=900&q=80",
      text: "Professional web design workspace",
      pos: "center",
      by: "Website responsif & performa tinggi",
    },
  },
  {
    common: "Chatbot AI",
    binomial: "WHATSAPP · WEBSITE · INSTAGRAM",
    photo: {
      url: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=900&q=80",
      text: "AI chatbot conversation interface",
      pos: "center",
      by: "Otomasi layanan pelanggan 24/7",
    },
  },
  {
    common: "Custom Web App",
    binomial: "PORTAL · DASHBOARD · CRM",
    photo: {
      url: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=900&q=80",
      text: "Custom web application development",
      pos: "center",
      by: "Aplikasi web skala enterprise",
    },
  },
  {
    common: "Manajemen Inventori",
    binomial: "STOK · LAPORAN · OTOMASI",
    photo: {
      url: "https://images.unsplash.com/photo-1553413077-190dd305871c?w=900&q=80",
      text: "Modern inventory management system",
      pos: "center",
      by: "Kontrol stok gudang berbasis cloud",
    },
  },
  {
    common: "Integrasi AI",
    binomial: "LLM · API · AUTOMATION",
    photo: {
      url: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=900&q=80",
      text: "Artificial intelligence technology",
      pos: "center",
      by: "Integrasi model AI ke sistem bisnis",
    },
  },
];

export default function LandingPage() {

  /* ── Scroll Reveal via IntersectionObserver ─────────────────────────── */
  useEffect(() => {
    const EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

    // Selectors — target meaningful child blocks only (not section wrappers)
    // so we avoid double-animating parent + children at the same time
    const selectors = [
      // Section-level headers (first visible element per section)
      '.cl-essence__header',
      '.cl-framework__header',
      '.cl-works__header',
      '.cl-protocol__title',
      // Individual repeating cards / items (staggered by sibling index)
      '.cl-essence__card',
      '.cl-bento',
      '.cl-works__project',
      '.cl-protocol__stage',
      // Inline section headers marked explicitly
      '[data-reveal]',
    ];

    const targets = document.querySelectorAll(selectors.join(', '));

    targets.forEach((el) => {
      // Compute stagger from sibling index
      const siblings = Array.from(el.parentElement?.children ?? []);
      const sibIdx = siblings.indexOf(el);
      const delay = sibIdx * 90;

      el.style.opacity = '0';
      el.style.transform = 'translateY(36px)';
      el.style.transition = [
        `opacity 0.82s ${EASING} ${delay}ms`,
        `transform 0.82s ${EASING} ${delay}ms`,
      ].join(', ');
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ── Header (dari structures) ── */}
      <Header activePage="home" />

      <main>

        {/* ══════════════════════════════
            HERO — Scroll Morph (full-screen cards)
        ══════════════════════════════ */}
        <section style={{
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
          background: '#060608',
        }}>
          <ScrollMorphHero />
        </section>

        {/* ══════════════════════════════
            PROJECT SHOWCASE
        ══════════════════════════════ */}
        <section style={{ padding: '6rem 0 7rem', background: '#080809', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {/* Header inside container */}
          <div className="cl-container">
            <div data-reveal style={{ marginBottom: '3rem' }}>
              <p style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.35)',
                marginBottom: '0.6rem',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
              }}>
                PORTOFOLIO
              </p>
              <h2 style={{
                fontFamily: 'var(--font-headline)',
                fontWeight: 700,
                fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                color: '#fff',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}>
                Kreasi Digital Kami
              </h2>
            </div>
          </div>

          {/* Full-bleed slider with fade masks */}
          <div style={{ position: 'relative' }}>
            {/* Left fade */}
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: 'clamp(3rem, 8vw, 8rem)',
              background: 'linear-gradient(to right, #080809, transparent)',
              zIndex: 2, pointerEvents: 'none',
            }} />
            {/* Right fade */}
            <div style={{
              position: 'absolute', right: 0, top: 0, bottom: 0,
              width: 'clamp(3rem, 8vw, 8rem)',
              background: 'linear-gradient(to left, #080809, transparent)',
              zIndex: 2, pointerEvents: 'none',
            }} />

            <InfiniteSlider
              gap={20}
              speed={35}
              pauseOnHover={true}
            >
              {[
                { src: process.env.PUBLIC_URL + '/img/project/alaisebythelake.webp', title: 'Restaurant Website' },
                { src: process.env.PUBLIC_URL + '/img/project/alaisebooking.webp', title: 'Booking System' },
                { src: process.env.PUBLIC_URL + '/img/project/farm.webp', title: 'Farm Management' },
                { src: process.env.PUBLIC_URL + '/img/project/inventory.webp', title: 'Inventory System' },
                { src: process.env.PUBLIC_URL + '/img/project/obscur.webp', title: 'Obscur Project' },
                { src: process.env.PUBLIC_URL + '/img/project/presensi.webp', title: 'Attendance System' },
              ].map((project, idx) => (
                <div
                  key={idx}
                  style={{
                    flexShrink: 0,
                    minWidth: '320px',
                    height: '200px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.06)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'border-color 0.3s ease, transform 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                  }}
                >
                  <img
                    src={project.src}
                    alt={project.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                  />
                  {/* Image caption overlay */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '1.5rem 1rem 0.8rem',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                  }}>
                    <span style={{
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '0.6rem',
                      color: 'rgba(255,255,255,0.6)',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {project.title}
                    </span>
                  </div>
                </div>
              ))}
            </InfiniteSlider>
          </div>
        </section>


        {/* ══════════════════════════════
            OUR ESSENCE
        ══════════════════════════════ */}
        <section className="cl-essence">
          <div className="cl-container">
            <div className="cl-essence__header">
              <h2 className="cl-essence__title">Esensi Kami</h2>
              <div className="cl-essence__divider" aria-hidden="true" />
              <p className="cl-essence__desc">
                Perpaduan antara integritas struktur logis dan keindahan intuitif dari desain.
              </p>
            </div>

            <div className="cl-essence__grid">
              <div className="cl-essence__card">
                <span className="material-icon cl-essence__icon">code</span>
                <h3>Code</h3>
                <p>
                  Fondasi dari logika. Kami memandang rekayasa sebagai seni presisi, menulis sistem yang bersih, skalabel, dan tangguh untuk bertahan dalam ujian waktu dan skala.
                </p>
              </div>
              <div className="cl-essence__card">
                <span className="material-icon cl-essence__icon">dark_mode</span>
                <h3>Lune</h3>
                <p>
                  Jiwa dari estetika. Dinamai dari bulan, melambangkan cahaya penuntun di dalam kekosongan. Kami menekankan keanggunan, intuisi pengguna, dan harmoni visual.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════
            L.U.N.E FRAMEWORK — Lightweight scroll
        ══════════════════════════════ */}
        <LuneScrollFX
          sections={[
            { id: 'logic',     leftLabel: 'Fondasi',  title: 'Logic',     rightLabel: 'Algoritma', background: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1920&q=80' },
            { id: 'utility',   leftLabel: 'Fungsi',   title: 'Utility',   rightLabel: 'Efisiensi', background: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80' },
            { id: 'network',   leftLabel: 'Koneksi',  title: 'Network',   rightLabel: 'Ekosistem', background: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80' },
            { id: 'execution', leftLabel: 'Rilis',    title: 'Execution', rightLabel: 'Performa',  background: 'https://images.unsplash.com/photo-1446776877081-d282a0f896e2?w=1920&q=80' },
          ]}
        />

        {/* ══════════════════════════════
            LAYANAN KAMI — Circular Gallery
        ══════════════════════════════ */}
        <section style={{
          background: '#060608',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          padding: 'clamp(3rem,6vw,6rem) 0 0',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div className="cl-container" data-reveal>
            <p style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.65rem',
              color: 'rgba(255,255,255,0.35)',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              marginBottom: '0.6rem',
            }}>LAYANAN</p>
            <h2 style={{
              fontFamily: 'var(--font-headline)',
              fontWeight: 700,
              fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
              color: '#fff',
              letterSpacing: '-0.03em',
              lineHeight: 1,
              marginBottom: '0.75rem',
            }}>Layanan Kami</h2>
            <p style={{
              fontSize: '0.82rem',
              color: 'rgba(255,255,255,0.4)',
              fontFamily: 'var(--font-body)',
              maxWidth: '32rem',
              lineHeight: 1.7,
            }}>
              Eksplor layanan yang kami tawarkan.
            </p>
          </div>

          {/* Gallery — height auto on mobile, 100vh on desktop */}
          <div style={{ width: '100%' }}>
            <style>{`
              @media (min-width: 768px) { .cg-wrap { height: 100vh; position: relative; } }
              @media (max-width: 767px) { .cg-wrap { height: auto; padding-bottom: 1rem; } }
            `}</style>
            <div className="cg-wrap">
              <CircularGallery
                items={SERVICES}
                radius={680}
                autoRotateSpeed={0.022}
              />
            </div>
          </div>
        </section>

        {/* ══════════════════════════════
            KARYA PILIHAN — Project Showcase
        ══════════════════════════════ */}
        <section style={{
          background: '#060608',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          padding: '6rem 0 7rem',
        }}>
          <div className="cl-container">
            {/* Header */}
            <div data-reveal style={{ marginBottom: '3.5rem' }}>
              <p style={{
                fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)',
                letterSpacing: '0.25em', fontFamily: "'Space Mono', monospace",
                textTransform: 'uppercase', marginBottom: '0.6rem',
              }}>KARYA PILIHAN</p>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{
                  fontFamily: 'var(--font-headline)',
                  fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
                  fontWeight: 700, color: '#fff',
                  letterSpacing: '-0.03em', lineHeight: 1, margin: 0,
                }}>Proyek Unggulan</h2>
                <a href="/portfolio" style={{
                  fontSize: '0.6rem', fontFamily: "'Space Mono', monospace",
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.35)',
                  textDecoration: 'none',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '5px 14px', borderRadius: '20px',
                  transition: 'color 0.2s, border-color 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                >LIHAT SEMUA →</a>
              </div>
            </div>

            {/* Showcase component */}
            <ProjectShowcase autoplay={true} />
          </div>
        </section>

        {/* ══════════════════════════════
            LOKASI — World Map
        ══════════════════════════════ */}
        <section style={{
          background: '#060608',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          padding: '5rem 0 6rem',
        }}>
          <div className="cl-container">
            {/* Header */}
            <div data-reveal style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <p style={{
                fontSize: '0.62rem', color: 'rgba(255,255,255,0.3)',
                letterSpacing: '0.25em', fontFamily: "'Space Mono', monospace",
                textTransform: 'uppercase', marginBottom: '0.75rem',
              }}>LOKASI</p>
              <h2 style={{
                fontFamily: 'var(--font-headline)',
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                fontWeight: 700, color: '#fff',
                letterSpacing: '-0.03em', lineHeight: 1,
                margin: '0 0 1rem',
              }}>Berbasis di Tomohon,<br />Melayani di Mana Saja</h2>
              <p style={{
                fontSize: '0.85rem', color: 'rgba(255,255,255,0.38)',
                fontFamily: "'Inter', sans-serif", lineHeight: 1.7,
                maxWidth: '28rem', margin: '0 auto',
              }}>
                Kami bekerja dari Tomohon, Sulawesi Utara — melayani klien
                di seluruh Indonesia dan mancanegara secara remote.
              </p>
            </div>

            {/* Map */}
            <WorldMap
              lineColor="#6366f1"
              dots={[
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: -33.8688, lng: 151.2093, label: 'Sydney' },
                },
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: 52.3676,  lng: 4.9041,  label: 'Amsterdam' },
                },
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: 40.7128,  lng: -74.0060, label: 'New York' },
                },
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: 35.6762,  lng: 139.6503, label: 'Tokyo' },
                },
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: -1.2921,  lng: 36.8219,  label: 'Nairobi' },
                },
                {
                  start: { lat: 1.3153,  lng: 124.8470, label: 'Tomohon' },
                  end:   { lat: -6.2088,  lng: 106.8456, label: 'Jakarta' },
                },
              ]}
            />

            {/* Location pill */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
              <span style={{
                fontSize: '0.62rem', fontFamily: "'Space Mono', monospace",
                letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.4)',
                background: 'rgba(99,102,241,0.12)',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '6px 18px', borderRadius: '20px',
              }}>
                📍 Tomohon, Sulawesi Utara, Indonesia
              </span>
            </div>
          </div>
        </section>

      </main>

      {/* ── Footer (dari structures) ── */}
      <Footer />
    </>
  );
}